// src/WebComponents/Profile/AccountScreen.jsx
import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { ChevronRight, Trash2, ArrowLeft, Pencil } from "lucide-react";
import { useDeleteAccount } from "../../hooks/useDeleteAccount";
import { useLogout } from "../../hooks/useAuth";

const AccountScreen = () => {
  const navigate = useNavigate();
  const [userData, setUserData] = useState({
    firstName: "Harsh",
    lastName: "Vaghasiya",
  });
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const { deleteAccount, isDeleting } = useDeleteAccount();
  const logout = useLogout();

  useEffect(() => {
    const loadUserData = () => {
      const stored = JSON.parse(localStorage.getItem("userData") || "{}");
      if (stored.firstName) setUserData(stored);
    };

    loadUserData();

    // Listen for user data updates
    window.addEventListener("userUpdated", loadUserData);
    return () => window.removeEventListener("userUpdated", loadUserData);
  }, []);

  const initials = `${userData.firstName?.[0] || ""}`.toUpperCase();
  const fullName =
    `${userData.firstName || ""} ${userData.lastName || ""}`.trim();

  const menuItems = [
    { label: "My Subscription", path: "/plan" },
    // { label: "Refer and earn", path: "/refer" },
    { label: "Terms & Conditions", path: "/terms" },
    { label: "Contact Us", path: "/contactus" },
    { label: "About Us", path: "/aboutus" },
  ];

  const handleLogout = () => {
    logout();
    localStorage.removeItem("userSubscription");
  };

  const handleDeleteAccount = async () => {
    const userId = localStorage.getItem("userData")
      ? JSON.parse(localStorage.getItem("userData")).id
      : null;
    const result = await deleteAccount(userId);

    if (!result.success) {
      alert(result.error || "Failed to delete account. Please try again.");
    }
    setShowConfirmModal(false);
  };

  // Confirmation Modal Component
  const ConfirmDeleteModal = () => (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 px-4">
      <div className="bg-white rounded-2xl max-w-sm w-full p-6 shadow-xl">
        <h3 className="text-xl font-bold text-gray-900 mb-3">
          Delete Account?
        </h3>
        <p className="text-gray-600 mb-6">
          Are you sure you want to delete your account? This action cannot be
          undone and all your data will be permanently lost.
        </p>
        <div className="flex gap-3">
          <button
            onClick={() => setShowConfirmModal(false)}
            className="flex-1 bg-gray-100 text-gray-700 font-semibold py-3 rounded-xl hover:bg-gray-200 transition-colors"
            disabled={isDeleting}
          >
            Cancel
          </button>
          <button
            onClick={handleDeleteAccount}
            className="flex-1 bg-red-500 text-white font-semibold py-3 rounded-xl hover:bg-red-600 transition-colors disabled:opacity-50"
            disabled={isDeleting}
          >
            {isDeleting ? "Deleting..." : "Delete"}
          </button>
        </div>
      </div>
    </div>
  );

  return (
    <div className="bg-white text-gray-900 flex flex-col">
      {/* Top Nav */}
      <div className="flex justify-between items-center px-5 pt-4 pb-2">
        <button
          onClick={() => navigate(-1)}
          className="bg-transparent border-0 text-gray-700 cursor-pointer p-0 hover:text-gray-900 transition-colors"
        >
          <ArrowLeft size={24} />
        </button>
        <button
          onClick={() => setShowConfirmModal(true)}
          className="bg-transparent border-0 text-red-500 cursor-pointer p-0 hover:text-red-600 transition-colors"
          aria-label="Delete account"
        >
          <Trash2 size={22} />
        </button>
      </div>

      {/* Avatar + Name */}
      <div className="flex flex-col items-center mt-6 mb-2">
        <div className="relative mb-3">
          <div className="w-20 h-20 rounded-full bg-gray-200 flex items-center justify-center text-3xl font-bold text-gray-700">
            {initials}
          </div>
          <button
            onClick={() => navigate("/edit-profile")}
            className="absolute bottom-0 right-0 w-7 h-7 rounded-full bg-[#c6f135] flex items-center justify-center border-0 cursor-pointer p-0 shadow-md hover:scale-105 transition-transform"
          >
            <Pencil size={13} color="#000" strokeWidth={2.5} />
          </button>
        </div>
        <p className="text-lg font-bold tracking-wide text-gray-800">
          Hi, {fullName}
        </p>
      </div>

      {/* Menu Items */}
      <div className="mt-8 flex-1">
        {menuItems.map((item, i) => (
          <button
            key={i}
            onClick={() => navigate(item.path)}
            className="w-full bg-transparent border-0 border-b border-gray-200 text-gray-700 text-base font-medium px-4 py-5 flex justify-between items-center cursor-pointer hover:bg-gray-50 transition-colors"
          >
            <span>{item.label}</span>
            <ChevronRight size={20} className="text-gray-400" />
          </button>
        ))}
        {/* Logout */}
        <div className="px-6 py-6 flex justify-center">
          <button
            onClick={handleLogout}
            className="bg-[#c6f135] text-black font-semibold text-sm px-5 py-2 rounded-full border-0 cursor-pointer hover:bg-[#b8e030] transition-colors shadow-sm"
          >
            Logout
          </button>
        </div>
      </div>

      {/* Confirmation Modal */}
      {showConfirmModal && <ConfirmDeleteModal />}
    </div>
  );
};

export default AccountScreen;
